import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup, ProgressBar, Badge, Spinner, DropdownButton, Dropdown } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import MediaDetailModal from './MediaDetailModal';

export default function MediaTracker() {
    const api = useApi();

    // Omni-Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [mediaType, setMediaType] = useState('Book');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [viewMode, setViewMode] = useState('Active');

    // Dashboard State
    const [activeItems, setActiveItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Initial Load
    useEffect(() => {
        fetchActiveItems();
        // eslint-disable-next-line
    }, []);

    const fetchActiveItems = async () => {
        setIsLoading(true);
        const res = await api.get('/media');
        if (res.ok) setActiveItems(res.body.items || []);
        setIsLoading(false);
    };

    // --- SEARCH EXTERNAL APIS ---
    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setIsSearching(true);
        const res = await api.get(`/media/search?q=${encodeURIComponent(searchQuery)}&type=${mediaType}`);
        if (res.ok) {
            setSearchResults(res.body.items || []);
        } else {
            alert("Search failed.");
        }
        setIsSearching(false);
    };

    // --- ADD TO TRACKER ---
    const handleAddMedia = async (item) => {
        const res = await api.post('/media', item);
        if (res.ok) {
            setSearchResults([]); // Clear search after adding
            setSearchQuery('');
            fetchActiveItems(); // Refresh dashboard
        } else {
            alert(res.body?.message || "Failed to add item.");
        }
    };

    // --- QUICK UPDATE PROGRESS ---
    const handleUpdateProgress = async (id, currentProgress, maxProgress, amount) => {
        let newProgress = currentProgress + amount;
        let status = 'In Progress';

        // ONLY cap the progress and auto-complete if a max_progress actually exists!
        if (maxProgress > 0) {
            if (newProgress >= maxProgress) {
                newProgress = maxProgress;
                status = 'Completed';
            }
        }

        // Optimistic UI update
        setActiveItems(prev => prev.map(item =>
            item.id === id ? { ...item, current_progress: newProgress, status: status } : item
        ));

        await api.put(`/media/${id}`, { current_progress: newProgress, status: status });
    };

    // 3. FILTER THE ITEMS BASED ON THE TAB
    const displayedItems = activeItems.filter(item => {
        if (viewMode === 'Active') {
            return item.status === 'In Progress' || item.status === 'Backlog';
        } else {
            return item.status === 'Completed' || item.status === 'Dropped';
        }
    });

    const formatProgress = (item, progressValue) => {
        if (item.media_type === 'Audiobook' || item.media_type === 'Movie') {
            const hrs = Math.floor(progressValue / 60);
            const mins = progressValue % 60;
            return `${hrs > 0 ? hrs + 'h ' : ''}${mins}m`;
        }
        return `${progressValue} ${item.progress_unit}`;
    };

    return (
        <Container className="py-4">
            <div className="mb-5 text-center">
                <h2 className="fw-bold mb-3"><i className="bi bi-collection-play text-primary me-2"></i> Media Tracker</h2>

                {/* OMNI-SEARCH BAR */}
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <Form onSubmit={handleSearch} className="position-relative">
                            <InputGroup size="lg" className="shadow-sm rounded-pill border bg-white">
                                <DropdownButton
                                    variant="light"
                                    title={mediaType}
                                    id="media-type-dropdown"
                                    className="border-end"
                                    onSelect={(selectedKey) => setMediaType(selectedKey)}
                                >
                                    <Dropdown.Item eventKey="Book"><i className="bi bi-book me-2"></i>Book</Dropdown.Item>
                                    <Dropdown.Item eventKey="Audiobook"><i className="bi bi-headphones me-2"></i>Audiobook</Dropdown.Item>
                                    <Dropdown.Item eventKey="Movie"><i className="bi bi-film me-2"></i>Movie</Dropdown.Item>
                                    <Dropdown.Item eventKey="TV"><i className="bi bi-tv me-2"></i>TV Series</Dropdown.Item>
                                    <Dropdown.Item eventKey="Game"><i className="bi bi-controller me-2"></i>Game</Dropdown.Item>
                                </DropdownButton>
                                <Form.Control
                                    type="text"
                                    className="border-0 bg-white"
                                    placeholder={`Search for a ${mediaType.toLowerCase()}...`}
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                <Button type="submit" variant="primary" className="px-4 fw-bold">
                                    {isSearching ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search"></i>}
                                </Button>
                            </InputGroup>

                            {/* SEARCH RESULTS OVERLAY */}
                            {searchResults.length > 0 && (
                                <Card className="position-absolute w-100 mt-2 shadow-lg border-0 rounded-4 z-3 text-start" style={{ top: '100%', left: 0 }}>
                                    <Card.Body className="p-2">
                                        <div className="d-flex justify-content-between align-items-center px-3 pt-2 pb-1 border-bottom">
                                            <span className="small fw-bold text-muted text-uppercase">Top Results</span>
                                            <Button variant="link" size="sm" className="text-muted p-0" onClick={() => setSearchResults([])}><i className="bi bi-x-lg"></i></Button>
                                        </div>
                                        {searchResults.map((item, idx) => (
                                            <div
                                                key={idx}
                                                className="d-flex align-items-center p-3 border-bottom hover-bg-light cursor-pointer transition-all"
                                                onClick={() => handleAddMedia(item)}
                                            >
                                                {item.cover_image_url ? (
                                                    <img src={item.cover_image_url} alt="Cover" className="rounded shadow-sm me-3" style={{ width: '40px', height: '60px', objectFit: 'cover' }} />
                                                ) : (
                                                    <div className="bg-secondary bg-opacity-25 rounded shadow-sm me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '60px' }}>
                                                        <i className="bi bi-image text-muted"></i>
                                                    </div>
                                                )}
                                                <div className="flex-grow-1">
                                                    <h6 className="fw-bold mb-0 text-dark">{item.title}</h6>
                                                    <span className="small text-muted">
                                                        {item.metadata?.authors?.join(', ')} • {item.max_progress} {item.progress_unit}
                                                    </span>
                                                </div>
                                                <Button variant="outline-primary" size="sm" className="rounded-pill px-3"><i className="bi bi-plus-lg"></i> Track</Button>
                                            </div>
                                        ))}
                                    </Card.Body>
                                </Card>
                            )}
                        </Form>
                    </Col>
                </Row>
            </div>

            {/* ACTIVE DASHBOARD */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-0">
                    {viewMode === 'Active' ? 'Active Dashboard' : 'The Vault'}
                </h5>
                <div className="bg-light p-1 rounded-pill border">
                    <Button
                        variant={viewMode === 'Active' ? 'primary' : 'light'}
                        size="sm"
                        className="rounded-pill px-3 fw-bold"
                        onClick={() => setViewMode('Active')}
                    >
                        Active
                    </Button>
                    <Button
                        variant={viewMode === 'Vault' ? 'primary' : 'light'}
                        size="sm"
                        className="rounded-pill px-3 fw-bold"
                        onClick={() => setViewMode('Vault')}
                    >
                        Vault
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : displayedItems.length === 0 ? (
                <div className="bg-light p-5 rounded-4 text-center border text-muted">
                    <i className={viewMode === 'Active' ? "bi bi-inbox display-4 mb-3 opacity-50" : "bi bi-archive display-4 mb-3 opacity-50"}></i>
                    <h4>{viewMode === 'Active' ? 'Your active tracker is empty' : 'Your vault is empty'}</h4>
                    <p>{viewMode === 'Active' ? 'Search for a book above to start building your library.' : 'Finish some books to see them archived here.'}</p>
                </div>
            ) : (
                <Row className="g-4">
                    {/* 5. SWAP 'activeItems' for 'displayedItems' in the map! */}
                    {displayedItems.map(item => {
                        const progressPercent = item.max_progress > 0 ? (item.current_progress / item.max_progress) * 100 : 0;

                        return (
                            <Col md={6} xl={4} key={item.id}>
                                <Card
                                    className="border-0 shadow-sm rounded-4 h-100 overflow-hidden animation-fade-in"
                                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setShowDetailModal(true);
                                    }}
                                >
                                    <div className="d-flex p-3">
                                        {/* Cover Art */}
                                        <div className="flex-shrink-0 me-3">
                                            {item.cover_image_url ? (
                                                <img src={item.cover_image_url} alt="Cover" className="rounded-3 shadow-sm" style={{ width: '80px', height: '120px', objectFit: 'cover' }} />
                                            ) : (
                                                <div className="bg-secondary bg-opacity-25 rounded-3 d-flex align-items-center justify-content-center" style={{ width: '80px', height: '120px' }}>
                                                    <i className="bi bi-book fs-3 text-muted"></i>
                                                </div>
                                            )}
                                        </div>

                                        {/* Meta & Actions */}
                                        <div className="flex-grow-1 d-flex flex-column justify-content-between">
                                            <div>
                                                <Badge bg="primary" className="mb-2">{item.media_type}</Badge>
                                                <h5 className="fw-bold mb-1 text-truncate" title={item.title}>{item.title}</h5>
                                                <p className="small text-muted mb-0 text-truncate">{item.metadata?.authors?.join(', ')}</p>
                                            </div>

                                            {/* CONTEXTUAL QUICK-INCREMENT ENGINE */}
                                            <div className="d-flex justify-content-end mt-2">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="rounded-pill fw-bold"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        // Define standard jumps per medium
                                                        let incrementAmount = 10; // Books default (+10 pages)
                                                        if (item.media_type === 'TV') incrementAmount = 1; // +1 Episode
                                                        if (item.media_type === 'Game') incrementAmount = 1; // +1 Hour (Games track in hours)
                                                        if (item.media_type === 'Audiobook') incrementAmount = 60; // +1 Hour (Audiobooks track in minutes!)
                                                        if (item.media_type === 'Movie') incrementAmount = item.max_progress - item.current_progress; // Finish movie

                                                        handleUpdateProgress(item.id, item.current_progress, item.max_progress, incrementAmount);
                                                    }}
                                                >
                                                    {item.media_type === 'Movie' ? (
                                                        <><i className="bi bi-check2-circle me-1"></i> Mark Watched</>
                                                    ) : (
                                                        `+${formatProgress(item, item.media_type === 'TV' || item.media_type === 'Game' ? 1 : (item.media_type === 'Audiobook' ? 60 : 10))}`
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer: Stars for Vault, Progress for Active */}
                                    {item.status === 'Completed' ? (
                                        <div className="bg-light p-3 border-top d-flex justify-content-between align-items-center">
                                            <div className="text-warning">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <i key={star} className={`bi bi-star${star <= (item.rating || 0) ? '-fill' : ''} me-1`}></i>
                                                ))}
                                            </div>
                                            <span className="small fw-bold text-success"><i className="bi bi-check-all"></i> Finished</span>
                                        </div>
                                    ) : (
                                        <div className="bg-light p-3 border-top">
                                            <div className="d-flex justify-content-between small fw-bold mb-1">
                                                <span className="text-primary">{formatProgress(item, item.current_progress)}</span>
                                                <span className="text-muted">{Math.round(progressPercent)}%</span>
                                            </div>
                                            <ProgressBar now={progressPercent} style={{ height: '6px' }} />
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
            <MediaDetailModal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                item={selectedItem}
                onSuccess={fetchActiveItems}
            />
        </Container>
    );
}
